import _ from 'lodash'
import { useEffect, useRef } from 'react'

import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import { useFilterStore } from 'features/filters/store'
import type { FilterStateV2 } from 'types/project'

/**
 * Projects a v2-native page's panel down to the global store's
 * `FilterStateV2` shape — a straight field pick, since both shapes carry the
 * same v2 filter vocabulary (no v1 round trip, unlike the old
 * `translatePanelToV1Filters`).
 */
export const panelToGlobalFilterState = (panel: ChartPanel): FilterStateV2 => ({
  filters: panel.filters,
  searchTerms: panel.searchTerms,
  searchCriteria: panel.searchCriteria,
  smartSearchEnabled: panel.smartSearchEnabled,
  dateRange: panel.dateRange,
})

/**
 * The inverse of `panelToGlobalFilterState` — applies a carried-over global
 * filter state onto an existing (or freshly-seeded) panel. A straight field
 * merge, same reasoning. `filters` needs a cast: the global store's
 * `SelectedFilterOption.field`/`operator` are loosely typed `string` (this
 * shared-layer type can't import charts-v2's precise field/operator unions),
 * but the values only ever originated from a real `ChartPanel` in the first
 * place (published by `panelToGlobalFilterState`, or read off a v2 hash
 * body), so the narrowing is safe.
 */
export const applyGlobalFilterStateToPanel = (
  state: FilterStateV2,
): Partial<ChartPanel> => ({
  filters: state.filters as ChartPanel['filters'],
  searchTerms: state.searchTerms,
  searchCriteria: state.searchCriteria,
  // An absent flag (the global store's initial state) means no smart search.
  // It must not be copied as `undefined`: pages seed a default panel with
  // `Object.assign`, which would overwrite the panel's `false`, and the
  // sidebars read `undefined` as "on" — an empty Smart search card.
  smartSearchEnabled: state.smartSearchEnabled ?? false,
  dateRange: state.dateRange,
})

/**
 * Publishes a v2-native page's `ChartPanel[]` into the shared v1
 * `useFilterStore` WHILE THE PAGE IS MOUNTED, on every panel change.
 *
 * This replaces the per-page unmount write-back. That could never work:
 * React commits the INCOMING route's render — where each page captures
 * `initialGlobalFiltersRef` — before the OUTGOING route's passive-effect
 * cleanup. So the incoming page snapshotted the global store before the
 * outgoing page published into it, then its overlay wrote that stale
 * snapshot back over the fresh value. Cross-page carry-over "worked
 * sometimes" only on a page's first-ever visit, where `panels.length === 0`
 * sends the seed path through the live hook-subscribed `filters` instead.
 *
 * Two guards, both load-bearing:
 *
 *  - MOUNT SKIP (`lastPublishedPanelsRef`, seeded with the mount-time array
 *    identity). On a hash-bearing mount the overlay hasn't run yet — it
 *    waits for the hydration fetch — so a mount-time publish would write
 *    this page's OWN STALE panels over the value the outgoing page just
 *    published. Nothing is lost by skipping: every mount path (the overlay,
 *    hash hydration, or the empty-store seed in `serialize`) mutates
 *    `panels` at least once afterward, so this effect still runs for it.
 *  - PROJECTION EQUALITY. Most panel edits that don't touch filters (chart
 *    type, metrics, breakdowns, title) produce an identical v2 projection;
 *    skipping those keeps `usePageFilterHash`'s writer from re-running for
 *    no reason.
 *
 * Deliberately keyed on `panels` ONLY — never on the store's own `filters`.
 * Keying on both would make any store-side write instantly overwritten by
 * this page's panels, which is exactly the feedback cycle this hook must
 * avoid.
 */
export const usePublishPanelsToGlobalFilters = (panels: ChartPanel[]): void => {
  const lastPublishedPanelsRef = useRef(panels)

  useEffect(() => {
    if (lastPublishedPanelsRef.current === panels) return
    lastPublishedPanelsRef.current = panels
    if (panels.length === 0) return

    const next: FilterStateV2[] = panels.map(panelToGlobalFilterState)
    if (_.isEqual(next, useFilterStore.getState().filters)) return

    useFilterStore.getState().setFilters(next)
  }, [panels])
}
